/*
 * This uses scroll positions to detect when an element becomes sticky.
 * It's most useful for detecting when a `position: sticky` element becomes stuck.
 * It's not useful for making an element `position: fixed` when sticky, because
 * the scroll position is only used to toggle a state class.
 */

const stickyElementSelector = '[data-sticky]'
const stickyClassname = 'is-stuck'
const blockLevelDisplayValues = new Set(['block', 'flex', 'grid', 'table', 'inline-block'])

let disconnectorCallbacks = []
let mutationObservers = []

function getBlockLevelAncestor(el) {
  let ancestor = el.parentElement

  while (ancestor) {
    if (blockLevelDisplayValues.has(getComputedStyle(ancestor).display)) {
      return ancestor
    }

    ancestor = ancestor.parentElement
  }

  return null
}

function getScrollableAncestor(el) {
  let ancestor = el.parentElement

  while (ancestor && ancestor !== document.body) {
    if (/(auto|scroll)/.test(getComputedStyle(ancestor).overflowY)) {
      return ancestor
    }

    ancestor = ancestor.parentElement
  }

  return window
}

function getRootTop(scrollRoot) {
  if (scrollRoot === window) {
    return 0
  }

  return scrollRoot.getBoundingClientRect().top
}

function setupStickyObserver(el) {
  if (el.hasAttribute('data-sticky-initialized')) {
    return
  }

  const style = getComputedStyle(el)
  const stickyOffset = parseInt(style.getPropertyValue('--sticky-offset'), 10)
  const stickyElementTop = Number.isNaN(stickyOffset) ? parseInt(style.top, 10) || 0 : stickyOffset
  const scrollRoot = getScrollableAncestor(el)
  const blockAncestor = getBlockLevelAncestor(el)
  let animationFrame = null

  const determineStickyState = () => {
    if (!el.isConnected) {
      return
    }

    const rootTop = getRootTop(scrollRoot)
    const stickyTop = rootTop + stickyElementTop
    const blockAncestorBottom = blockAncestor ? blockAncestor.getBoundingClientRect().bottom : Infinity
    const shouldBeStuck =
      el.getBoundingClientRect().top <= stickyTop && stickyTop + el.offsetHeight < blockAncestorBottom

    el.classList.toggle(stickyClassname, shouldBeStuck)
  }

  const scheduleStickyState = () => {
    if (animationFrame !== null) {
      return
    }

    animationFrame = window.requestAnimationFrame(() => {
      animationFrame = null
      determineStickyState()
    })
  }

  determineStickyState()
  scheduleStickyState()
  scrollRoot.addEventListener('scroll', scheduleStickyState)
  disconnectorCallbacks.push(() => {
    scrollRoot.removeEventListener('scroll', scheduleStickyState)
  })

  el.setAttribute('data-sticky-initialized', '1')
}

function setupStickyObservers(container) {
  if (!container) {
    return
  }

  // Initialize existing elements:

  container.querySelectorAll(stickyElementSelector).forEach((el) => {
    setupStickyObserver(el)
  })

  // Observe new elements:

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) {
          return
        }

        if (node.matches(stickyElementSelector)) {
          setupStickyObserver(node)
        }

        node.querySelectorAll(stickyElementSelector).forEach((el) => {
          setupStickyObserver(el)
        })
      })
    })
  })

  observer.observe(container, {
    childList: true,
    subtree: true,
    attributes: false,
    characterData: false,
  })
  mutationObservers.push(observer)
}

function destroyStickyObservers() {
  mutationObservers.forEach((observer) => observer.disconnect())
  mutationObservers = []

  disconnectorCallbacks.forEach((cb) => {
    cb()
  })
  disconnectorCallbacks = []
}

export { setupStickyObservers, destroyStickyObservers }
